/**
 * Framework-agnostic OAuth2 (authorization code + PKCE) client for the
 * Bitwave IdP at auth.bitwave.io. No Vue/axios/browser hard dependencies:
 * every environment touchpoint is an injectable port with a browser default,
 * so the whole flow is unit-testable and reusable across apps (wavie-app,
 * web-app-v3, future SPAs).
 */

export type ScopeKey = "base" | `org:${string}`;

export interface AuthClientConfig {
  /** IdP origin, e.g. https://auth.bitwave.io */
  authBaseUrl: string;
  /** OAuth2 public client id registered on auth-svc, e.g. "wavie-app" */
  clientId: string;
  /** Absolute redirect URI registered for the client, e.g. https://app.wavie.ai/auth/callback */
  redirectUri: string;
  /** Persistent storage (survives redirects/tabs). Default: window.localStorage. */
  storage?: KV;
  /** Per-tab storage for the redirect loop breaker. Default: window.sessionStorage. */
  volatileStorage?: KV;
  clock?: Clock;
  http?: HttpPort;
  locks?: LockPort;
  broadcast?: BroadcastPort;
  /** Full-page navigation. Default: window.location.assign. */
  navigate?: (url: string) => void;
  randomBytes?: (n: number) => Uint8Array;
  sha256?: (data: Uint8Array) => Promise<Uint8Array>;
  /** Redirects to the IdP allowed per window before the loop breaker trips. Default 3 per 2 min. */
  maxRedirects?: number;
  redirectWindowMs?: number;
  /** Refresh when the access token is within this many ms of (skew-adjusted) expiry. Default 30s. */
  expiryLeewayMs?: number;
  /** Minimum ms between failed refresh attempts (hot-loop floor). Default 10s. */
  refreshFailureFloorMs?: number;
  /**
   * Consecutive opaque (fetch threw: network down, DNS, an ingress error page
   * without CORS headers) refresh failures before the session is ended.
   * Default Infinity: an opaque failure is treated as the IdP being
   * unreachable, never as proof the chain is dead — the IdP sends CORS
   * headers on its 4xx, so a real dead chain always arrives as a readable
   * `invalid_grant`. Set a finite number only for an IdP that hides its 4xx.
   */
  maxOpaqueRefreshFailures?: number;
  /** Cap for the auto-refresh backoff between consecutive failed attempts. Default 5 min. */
  autoRefreshMaxBackoffMs?: number;
  events?: AuthEvents;
}

export interface AuthEvents {
  /** The refresh-token chain is dead; the app should offer/perform an interactive login. */
  onAuthRequired?: (info: { reason: string }) => void;
  /** Tokens were adopted or cleared (local or from another tab). */
  onSessionChanged?: (session: AuthSession | null) => void;
}

/** Minimal synchronous key-value port (localStorage-shaped). */
export interface KV {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
  keys(): string[];
}

export interface Clock {
  now(): number;
}

export interface HttpPort {
  /** POST a JSON body. Must NOT throw on non-2xx; resolve with status + parsed body. */
  postJson(
    url: string,
    body: unknown,
    opts?: { credentials?: boolean },
  ): Promise<{ status: number; data: TokenEndpointResponse | null }>;
  /** Best-effort GET (used for IdP logout). Never throws. */
  get(url: string, opts?: { credentials?: boolean }): Promise<void>;
}

export interface LockPort {
  /** Run fn under an exclusive cross-tab lock. */
  withLock<T>(name: string, fn: () => Promise<T>): Promise<T>;
}

export interface BroadcastPort {
  post(message: string): void;
  subscribe(fn: (message: string) => void): () => void;
}

export interface TokenEndpointResponse {
  access_token?: string;
  id_token?: string;
  refresh_token?: string;
  expires_in?: number;
  scope?: string;
  token_type?: string;
  error?: string;
  error_description?: string;
}

export interface AccessClaims {
  sub: string;
  iat: number;
  exp: number;
  jti?: string;
  orgId?: string;
  userId?: string;
  scopes?: string[];
  loginMethod?: string;
}

export interface IdClaims {
  sub: string;
  iat: number;
  exp: number;
  email?: string;
  /**
   * Firebase email-verification state. ABSENT on tokens minted before the
   * claim shipped, so consumers must treat `undefined` as "unknown, do not
   * gate" and only act on an explicit `false`.
   */
  email_verified?: boolean;
  name?: string;
}

export interface StoredTokens {
  accessToken: string;
  idToken?: string;
  /** serverTime - clientTime estimated from the access token's iat at adoption. */
  skewMs: number;
  obtainedAtMs: number;
}

export interface AuthSession {
  principalId: string;
  email?: string;
  /**
   * Email-verification state from the ID token. `undefined` means unknown
   * (pre-claim token) — never gate on undefined, only on an explicit `false`.
   */
  emailVerified?: boolean;
  name?: string;
  /** Org the current access token is bound to; undefined for an org-less base session. */
  orgId?: string;
}

export interface PendingAuthRequest {
  verifier: string;
  redirectUri: string;
  createdAtMs: number;
  orgId?: string;
  returnPath?: string;
  /**
   * JSON-serializable app state bound to this authorize request. It lives in
   * the state-keyed record (persistent storage), so it reaches the callback
   * even when the login finishes in another tab — an email-verification link
   * opens one — where the starting tab's sessionStorage is not visible.
   */
  appState?: unknown;
}

export type CallbackResult =
  | { ok: true; session: AuthSession; returnPath?: string; appState?: unknown }
  | { ok: false; error: AuthFlowError };

export class AuthFlowError extends Error {
  readonly code: string;
  readonly description?: string;
  /** Terminal errors must surface to the user; retrying/redirecting cannot fix them. */
  readonly terminal: boolean;

  constructor(
    code: string,
    opts?: { description?: string; terminal?: boolean },
  ) {
    super(opts?.description ? `${code}: ${opts.description}` : code);
    this.name = "AuthFlowError";
    this.code = code;
    this.description = opts?.description;
    this.terminal = opts?.terminal ?? false;
  }
}

/** The principal's session is fine, but a token for the requested org cannot be minted. */
export class OrgAccessError extends AuthFlowError {
  readonly orgId: string;

  constructor(orgId: string, code: string, description?: string) {
    super(code, { description, terminal: true });
    this.name = "OrgAccessError";
    this.orgId = orgId;
  }
}

export function scopeKeyFor(orgId?: string): ScopeKey {
  return orgId ? `org:${orgId}` : "base";
}
